import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AnalyticsFilters } from '@ederest/contracts';

const DAY_MS = 86_400_000;
export const PERIOD_PRESETS = [7, 30, 90] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

const FILTER_KEYS = [
  'from',
  'to',
  'journeyId',
  'stepId',
  'fieldKey',
  'language',
  'viewport',
  'configurationVersion',
  'entityKey',
  'announcementType',
] as const;

type FilterKey = (typeof FILTER_KEYS)[number];

/** Serialises the filters as a query string, in a stable order (a stable TanStack Query key). */
export function toQueryString(filters: AnalyticsFilters): string {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  return params.toString();
}

/**
 * The combinable filters live in the URL search parameters (functional §5.4):
 * a filtered view can be shared or reloaded as it is.
 */
export function useAnalyticsFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<AnalyticsFilters>(() => {
    const read = (key: FilterKey) => params.get(key) ?? undefined;
    const version = read('configurationVersion');
    const viewport = read('viewport');
    return {
      from: read('from'),
      to: read('to'),
      journeyId: read('journeyId'),
      stepId: read('stepId'),
      fieldKey: read('fieldKey'),
      language: read('language'),
      viewport:
        viewport === 'mobile' || viewport === 'tablet' || viewport === 'desktop' ? viewport : undefined,
      configurationVersion: version ? Number(version) : undefined,
      entityKey: read('entityKey'),
      announcementType: read('announcementType'),
    };
  }, [params]);

  const set = useCallback(
    (patch: Partial<Record<FilterKey, string | undefined>>) => {
      // The live location, not the params of the last render: two updates in the same
      // tick (a reset then a selection) would otherwise revive the criteria just cleared.
      const current = new URLSearchParams(window.location.search);
      setParams(
        () => {
          const next = new URLSearchParams(current);
          // A journey change invalidates the step and field chosen under the previous
          // journey, a step change the field: cleared first, so the patch itself wins.
          if ('journeyId' in patch && patch.journeyId !== current.get('journeyId')) {
            next.delete('stepId');
            next.delete('fieldKey');
          }
          if ('stepId' in patch && patch.stepId !== current.get('stepId')) next.delete('fieldKey');
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setPeriod = useCallback(
    (days: PeriodPreset) => {
      const to = new Date();
      const from = new Date(to.getTime() - days * DAY_MS);
      set({ from: from.toISOString(), to: to.toISOString() });
    },
    [set],
  );

  const reset = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);

  const activeCount = FILTER_KEYS.filter((key) => params.has(key)).length;

  /** The preset whose length matches the period in the URL, if any. */
  const activePreset = useMemo<PeriodPreset | null>(() => {
    if (!filters.from || !filters.to) return 30;
    const days = Math.round((Date.parse(filters.to) - Date.parse(filters.from)) / DAY_MS);
    return PERIOD_PRESETS.find((preset) => preset === days) ?? null;
  }, [filters.from, filters.to]);

  return { filters, set, setPeriod, reset, activeCount, activePreset, queryString: toQueryString(filters) };
}
